import type { ButtonStyle } from "@codway-dev/ui";
import type { BackgroundImagePreset } from "./DownsellEditorConfig";
import { BACKGROUND_PRESETS } from "./DownsellEditorConfig";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DownsellPreviewProps {
  backgroundImagePreset: BackgroundImagePreset;
  backgroundColor: string;
  borderRadius: number;
  borderWidth: number;
  borderColor: string;
  titleTemplate: string;
  titleColor: string;
  titleSize: number;
  subtitleTemplate: string;
  subtitleColor: string;
  subtitleSize: number;
  discountBadgeText: string;
  discountBadgeTextColor: string;
  discountBadgeDiscountColor: string;
  discountBadgeSize: number;
  pricingAction: string;
  pricingValue: number;
  ctaAcceptStyle: ButtonStyle;
  ctaDeclineStyle: ButtonStyle;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveBackground(
  preset: BackgroundImagePreset,
  color: string
): string {
  if (preset === "none") return color || "#ffffff";
  const found = BACKGROUND_PRESETS.find((p) => p.id === preset);
  return found ? found.gradient : color || "#ffffff";
}

function formatDiscountLabel(action: string, value: number): string {
  switch (action) {
    case "percentage_off":
      return `${value}%`;
    case "fixed_amount_off":
      return `$${value}`;
    case "fixed_price_per_item":
      return `$${value}`;
    default:
      return "";
  }
}

function resolveButtonText(text: string, discountLabel: string): string {
  return text.replace(/\{discount\}/g, discountLabel);
}

// ─── Preview ─────────────────────────────────────────────────────────────────

export function DownsellEditorPreview({
  backgroundImagePreset,
  backgroundColor,
  borderRadius,
  borderWidth,
  borderColor,
  titleTemplate,
  titleColor,
  titleSize,
  subtitleTemplate,
  subtitleColor,
  subtitleSize,
  discountBadgeText,
  discountBadgeTextColor,
  discountBadgeDiscountColor,
  discountBadgeSize,
  pricingAction,
  pricingValue,
  ctaAcceptStyle,
  ctaDeclineStyle,
}: DownsellPreviewProps) {
  const background = resolveBackground(backgroundImagePreset, backgroundColor);
  const discountLabel = formatDiscountLabel(pricingAction, pricingValue);
  const acceptText = resolveButtonText(ctaAcceptStyle.text, discountLabel);
  const declineText = ctaDeclineStyle.text;

  return (
    <div className="sticky top-4">
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-icon type="view" />
            <s-text type="strong">Live preview:</s-text>
          </s-stack>

          {/* Modal preview */}
          <div
            style={{
              background,
              borderRadius: `${borderRadius}px`,
              borderWidth: `${borderWidth}px`,
              borderColor,
              borderStyle: borderWidth > 0 ? "solid" : "none",
              overflow: "hidden",
              minHeight: "300px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px 16px",
              gap: "12px",
            }}
          >
            {/* Title */}
            {titleTemplate && (
              <p
                style={{
                  color: titleColor,
                  fontSize: `${titleSize}px`,
                  fontWeight: 600,
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {titleTemplate}
              </p>
            )}

            {/* Subtitle */}
            {subtitleTemplate && (
              <p
                style={{
                  color: subtitleColor,
                  fontSize: `${subtitleSize}px`,
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {subtitleTemplate}
              </p>
            )}

            {/* Discount badge */}
            <div
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {discountBadgeText && (
                <p
                  style={{
                    color: discountBadgeTextColor,
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    textAlign: "center",
                    margin: 0,
                    maxWidth: "200px",
                  }}
                >
                  {discountBadgeText}
                </p>
              )}
              {discountLabel && (
                <p
                  style={{
                    color: discountBadgeDiscountColor,
                    fontSize: `${discountBadgeSize}px`,
                    fontWeight: 900,
                    lineHeight: 1,
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {discountLabel}
                </p>
              )}
            </div>

            {/* Accept button */}
            <button
              style={{
                background: ctaAcceptStyle.backgroundColor,
                color: ctaAcceptStyle.textColor,
                borderColor: ctaAcceptStyle.borderColor,
                borderWidth: `${ctaAcceptStyle.borderWidth}px`,
                borderStyle: "solid",
                borderRadius: `${ctaAcceptStyle.borderRadius}px`,
                fontSize: `${ctaAcceptStyle.textSize}px`,
                fontWeight: 700,
                textTransform: "uppercase",
                padding: "10px 16px",
                width: "100%",
                cursor: "default",
                boxShadow:
                  ctaAcceptStyle.shadow > 0
                    ? `0 ${ctaAcceptStyle.shadow}px ${ctaAcceptStyle.shadow * 2}px rgba(0,0,0,0.2)`
                    : "none",
                textAlign: "center",
              }}
            >
              {acceptText}
            </button>

            {/* Decline button */}
            <button
              style={{
                background: ctaDeclineStyle.backgroundColor,
                color: ctaDeclineStyle.textColor,
                borderColor: ctaDeclineStyle.borderColor,
                borderWidth: `${ctaDeclineStyle.borderWidth}px`,
                borderStyle: "solid",
                borderRadius: `${ctaDeclineStyle.borderRadius}px`,
                fontSize: `${ctaDeclineStyle.textSize}px`,
                fontWeight: 500,
                padding: "10px 16px",
                width: "100%",
                cursor: "default",
                boxShadow:
                  ctaDeclineStyle.shadow > 0
                    ? `0 ${ctaDeclineStyle.shadow}px ${ctaDeclineStyle.shadow * 2}px rgba(0,0,0,0.15)`
                    : "none",
                textAlign: "center",
              }}
            >
              {declineText}
            </button>
          </div>
        </s-stack>
      </s-box>
    </div>
  );
}
